import { usePreferenceStore } from '@/store/preferenceStore'
import { storeToRefs } from 'pinia'
import {
  position,
  initialCalibration,
  updatedCalibration,
  noMotorsCalibration,
} from '@/types/position'

export default class {
  static validateMotorPosition(motorPosition: position): boolean {
    const preferenceStore = usePreferenceStore()
    const { hasPositioningMotors } = storeToRefs(preferenceStore)

    if (!hasPositioningMotors.value) {
      return true
    }

    const bounds = {
      handlebarX: { min: 0, max: 325 },
      handlebarY: { min: 0, max: 492 },
      saddleX: { min: 0, max: 220 },
      saddleY: { min: 0, max: 412 },
    }

    for (const axis in bounds) {
      const value = motorPosition[axis as keyof position]
      const { min, max } = bounds[axis as keyof typeof bounds]
      if (value < min || value > max) {
        console.warn(
          `validateMotorPosition failed for ${axis}: ${value} not in [${min}, ${max}]`,
        )
        return false
      }
    }

    return true
  }

  static async initialCalibration(
    positionDeviation: position,
    motorPosition: position,
    startPosition: position,
  ): Promise<initialCalibration | false> {
    const newPositionOffset: position = {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    }
    const newMotorPosition: position = {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    }

    newPositionOffset.handlebarX =
      positionDeviation.handlebarX * -1 - motorPosition.handlebarX
    newPositionOffset.handlebarY =
      positionDeviation.handlebarY - motorPosition.handlebarY
    newPositionOffset.saddleX =
      positionDeviation.saddleX - motorPosition.saddleX
    newPositionOffset.saddleY =
      positionDeviation.saddleY - motorPosition.saddleY

    newMotorPosition.handlebarX =
      startPosition.handlebarX - newPositionOffset.handlebarX
    newMotorPosition.handlebarY =
      startPosition.handlebarY - newPositionOffset.handlebarY
    newMotorPosition.saddleX = startPosition.saddleX - newPositionOffset.saddleX
    newMotorPosition.saddleY = startPosition.saddleY - newPositionOffset.saddleY

    if (this.validateMotorPosition(newMotorPosition)) {
      return {
        positionOffset: newPositionOffset,
        motorPosition: newMotorPosition,
      }
    } else {
      return false
    }
  }

  static async updatedCalibration(
    positionDeviation: position,
    motorPosition: position,
    absolutePosition: position,
  ): Promise<updatedCalibration | false> {
    const newMotorPosition: position = {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    }
    const positionChange: position = {
      handlebarX: 0,
      handlebarY: 0,
      saddleX: 0,
      saddleY: 0,
    }

    positionChange.handlebarX =
      positionDeviation.handlebarX * -1 - absolutePosition.handlebarX
    positionChange.handlebarY =
      positionDeviation.handlebarY - absolutePosition.handlebarY
    positionChange.saddleX =
      positionDeviation.saddleX - absolutePosition.saddleX
    positionChange.saddleY =
      positionDeviation.saddleY - absolutePosition.saddleY

    newMotorPosition.handlebarX =
      motorPosition.handlebarX - positionChange.handlebarX
    newMotorPosition.handlebarY =
      motorPosition.handlebarY - positionChange.handlebarY
    newMotorPosition.saddleX = motorPosition.saddleX - positionChange.saddleX
    newMotorPosition.saddleY = motorPosition.saddleY - positionChange.saddleY

    if (this.validateMotorPosition(newMotorPosition)) {
      return {
        motorPosition: newMotorPosition,
        positionChange: positionChange,
      }
    } else {
      return false
    }
  }

  static async noMotorsCalibration(
    positionDeviation: position,
  ): Promise<noMotorsCalibration | false> {
    if (this.validateMotorPosition(positionDeviation)) {
      return {
        motorPosition: positionDeviation,
      }
    } else {
      return false
    }
  }
}
